import styled from 'styled-components';
import { useGTM } from '../../hooks/useGTM';

interface ButtonProps {
  $variant?: 'primary' | 'secondary' | 'outline';
  size?: 'small' | 'medium' | 'large';
  fullWidth?: boolean;
  children: React.ReactNode;
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
}

export const Button = ({ children, onClick, ...props }: ButtonProps) => {
  const { pushEvent } = useGTM();

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    pushEvent('button_click', {
      button_text: typeof children === 'string' ? children : 'button',
      button_type: props.$variant,
      page: window.location.pathname
    });

    if (onClick) {
      onClick(e);
    }
  };

  return (
    <StyledButton {...props} onClick={handleClick}>
      {children}
    </StyledButton>
  );
};

const StyledButton = styled.button<ButtonProps>`
  padding: ${({ theme, size = 'medium' }) => {
    switch (size) {
      case 'small': return `${theme.spacing.xs} ${theme.spacing.sm}`;
      case 'large': return `${theme.spacing.md} ${theme.spacing.xl}`;
      default: return `${theme.spacing.sm} ${theme.spacing.md}`;
    }
  }};
  
  font-size: ${({ theme, size = 'medium' }) => {
    switch (size) {
      case 'small': return theme.typography.sizes.sm;
      case 'large': return theme.typography.sizes.lg;
      default: return theme.typography.sizes.md;
    }
  }};
  
  ${({ $variant = 'primary', theme }) => {
    switch ($variant) {
      case 'secondary':
        return `
          background: ${theme.colors.secondary.silver};
          color: ${theme.colors.text.primary};
          border: none;
          
          &:hover {
            background: ${theme.colors.secondary.copper};
          }
        `;
      case 'outline':
        return `
          background: transparent;
          color: ${theme.colors.primary.main};
          border: 2px solid ${theme.colors.primary.main};
          
          &:hover {
            background: ${theme.colors.primary.main}20;
          }
        `;
      default:
        return `
          background: ${theme.colors.primary.main};
          color: ${theme.colors.text.primary};
          border: none;
          
          &:hover {
            background: ${theme.colors.primary.dark};
          }
        `;
    }
  }}
  
  width: ${({ fullWidth }) => fullWidth ? '100%' : 'auto'};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-family: ${({ theme }) => theme.typography.fontFamily.heading};
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`; 